import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { StatsFilterModal } from './StatsFilterModal';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';

export const StatsTable = ({ activeTab, followedPlayers = [] }) => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const [filterActiveTab, setFilterActiveTab] = useState(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleMessageClick = (player) => {
    const rolePrefix = location.pathname.split('/')[1] || 'coach';
    const nameParts = (player.name || '').split(' ');
    const playerPayload = {
      _id: player._id,
      firstName: player.firstName || nameParts[0] || '',
      lastName: player.lastName || nameParts.slice(1).join(' ') || '',
      profileImage: player.profileImage || null,
    };
    navigate(`/${rolePrefix}/messages`, { state: { newConversationPlayer: playerPayload } });
  };

  // Define stat column mappings
  const statColumnMapping = {
    // Hitting stats
    'avg': { label: 'AVG', key: 'avg' },
    'obp': { label: 'OBP', key: 'ob_pct' },
    'slg': { label: 'SLG', key: 'slg_pct' },
    'ops': { label: 'OPS', key: 'ops', calculated: true },
    'ops+': { label: 'OPS+', key: 'ops_plus' },
    'runs': { label: 'R', key: 'r' },
    'hits': { label: 'H', key: 'h' },
    
    '2b': { label: '2B', key: '2b' },
    '3b': { label: '3B', key: '3b' },
    'hr': { label: 'HR', key: 'hr' },
    'rbi': { label: 'RBI', key: 'rbi' },
    'tb': { label: 'TB', key: 'tb' },
    'bb': { label: 'BB', key: 'bb' },
    'k': { label: 'K', key: 'so' },
    'k%': { label: 'K%', key: 'k_pct', calculated: true },
    'bb%': { label: 'BB%', key: 'bb_pct', calculated: true },
    'k/bb': { label: 'K/BB', key: 'k_bb_ratio' },
    'sb': { label: 'SB', key: 'sb' },
    'cs': { label: 'CS', key: 'cs' },
    'iso': { label: 'ISO', key: 'iso' },
    'woba': { label: 'wOBA', key: 'woba' },
    'wrc+': { label: 'wRC+', key: 'wrc_plus' },
    'pa': { label: 'PA', key: 'pa' },
    'babip': { label: 'BABIP', key: 'babip' },
    // Pitching stats
    'w': { label: 'W', key: 'wins' },
    'l': { label: 'L', key: 'losses' },
    'era': { label: 'ERA', key: 'era' },
    'whip': { label: 'WHIP', key: 'WHIP' },
    'ip': { label: 'IP', key: 'innings_pitched' },
    'so': { label: 'SO', key: 'strikeouts_pitched' },
    'gs': { label: 'GS', key: 'games_started' },
    'app': { label: 'APP', key: 'games_pitched' },
    'cg': { label: 'CG', key: 'complete_games' },
    'sho': { label: 'SHO', key: 'shutouts' },
    'sv': { label: 'SV', key: 'saves' },
    'h': { label: 'H', key: 'hits_allowed' },
    'r': { label: 'R', key: 'runs_allowed' },
    'er': { label: 'ER', key: 'earned_runs' },
    'baa': { label: 'BAA', key: 'batting_average_against' },
    'ab': { label: 'AB', key: 'at_bats' },
    'wp': { label: 'WP', key: 'wild_pitches' },
    'hbp': { label: 'HBP', key: 'hit_batters' },
    'bk': { label: 'BK', key: 'balks' },
    'fip': { label: 'FIP', key: 'fip' },
    'k-bb%': { label: 'K-BB%', key: 'k_bb_pct' },
    'fip+': { label: 'FIP+', key: 'fip_plus' },
    'era+': { label: 'ERA+', key: 'era_plus' },
    'xfip': { label: 'xFIP', key: 'xfip' },
    'lob%': { label: 'LOB%', key: 'lob_pct' },
    'babip-against': { label: 'BABIP Against', key: 'babip_against' },
  };

  // Default columns (always visible)
  const fixedColumns = ['Name', 'Position', 'Team', 'Class'];

  // Determine dynamic stat columns based on filters
  const getDynamicColumns = () => {
    if (selectedStatColumns.length > 0) {
      return selectedStatColumns.map(col => statColumnMapping[col]).filter(Boolean);
    }

    // Default columns when no filters are selected
    if (activeTab === 'hitting') {
      return [
        { label: 'Games', key: 'gp' },
        { label: 'AVG', key: 'avg' },
        { label: 'OBP', key: 'ob_pct' },
        { label: 'SLG', key: 'slg_pct' },
        { label: 'OPS', key: 'ops', calculated: true },
        { label: 'R', key: 'r' },
        { label: 'H', key: 'h' },
        { label: '2B', key: '2b' },
        { label: '3B', key: '3b' },
        { label: 'HR', key: 'hr' },
        { label: 'RBI', key: 'rbi' },
        { label: 'TB', key: 'tb' },
        { label: 'BB%', key: 'bb_pct', calculated: true },
        { label: 'K%', key: 'k_pct', calculated: true },
      ];
    } else {
      return [
        { label: 'W', key: 'wins' },
        { label: 'L', key: 'losses' },
        { label: 'ERA', key: 'era' },
        { label: 'Games', key: 'games_pitched' },
        { label: 'CG', key: 'complete_games' },
        { label: 'SHO', key: 'shutouts' },
        { label: 'SV', key: 'saves' },
        { label: 'IP', key: 'innings_pitched' },
        { label: 'H', key: 'hits_allowed' },
        { label: 'R', key: 'runs_allowed' },
        { label: 'ER', key: 'earned_runs' },
        { label: 'BB%', key: 'bb_pct', calculated: true },
        { label: 'K%', key: 'k_pct', calculated: true },
        { label: 'HR', key: 'home_runs_allowed' },
      ];
    }
  };

  const dynamicColumns = getDynamicColumns();
  const allColumns = [...fixedColumns, ...dynamicColumns.map(col => col.label)];

  const formatStat = (value, columnKey) => {
    if (value === null || value === undefined || value === '') return '0';

    // Handle MongoDB Decimal128 objects
    if (typeof value === 'object' && value.$numberDecimal) {
      value = parseFloat(value.$numberDecimal);
    }

    if (typeof value === 'number') {
      if (columnKey === 'slg_pct' || columnKey === 'slugging_percentage' || columnKey === 'ob_pct' || columnKey === 'on_base_percentage' || columnKey === 'avg' || columnKey === 'batting_average' || columnKey === 'batting_average_against' || columnKey === 'baa') return value.toFixed(3);
      if (value === 0) return '0';
      if (columnKey === 'era' || columnKey === 'ERA' || columnKey === 'xfip' || columnKey === 'xFIP' || columnKey === 'WHIP' || columnKey === 'whip') return value.toFixed(2);
      if (columnKey === 'ops' || columnKey === 'on_base_plus_slugging') return value.toFixed(3);
      if (columnKey === 'ops_plus' || columnKey === 'wrc_plus') return Math.round(value).toString();
      if (columnKey === 'innings_pitched' || columnKey === 'ip' || columnKey === 'inningsPitched') return value.toFixed(1);
      return value % 1 === 0 ? value : value.toFixed(3);
    }
    return value;
  };

  const calculateOPS = (obp, slg) => {
    const obpVal = parseFloat(obp) || 0;
    const slgVal = parseFloat(slg) || 0;
    return (obpVal + slgVal).toFixed(3);
  };

  const handleApplyFilters = (filters) => {
    console.log('Applied filters:', filters);

    // Store selected filters for dynamic column display
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
      setFilterActiveTab(filters.activeTab); // Track which tab the filters are from
    } else {
      setSelectedStatColumns([]);
      setFilterActiveTab(null);
    }
  };

  // Helper function to get stat value from player data
  const getStatValue = (player, column) => {
    // Determine which tab to use: filterActiveTab takes precedence over activeTab
    const currentTab = filterActiveTab || activeTab;

    // Get stats - handle both array and object formats
    let stats;

    // Check if this is a fielding stat
    if (column.isFielding) {
      stats = Array.isArray(player.fieldingStats)
        ? player.fieldingStats[0]
        : player.fieldingStats;
    } else if (currentTab === 'hitting') {
      // battingStats can be an array or object
      stats = Array.isArray(player.battingStats)
        ? player.battingStats[0]
        : player.battingStats;
    } else {
      // pitchingStats can be an array or object
      stats = Array.isArray(player.pitchingStats)
        ? player.pitchingStats[0]
        : player.pitchingStats;
    }

    // K%/BB% — read directly from backend
    const fmtPct = (v) => {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return n > 1 ? `${n.toFixed(1)}%` : `${(n * 100).toFixed(1)}%`;
    };
    if (column.calculated && column.key === 'k_pct') {
      const v = currentTab === 'pitching' ? stats?.K_percent : stats?.k_percentage;
      return fmtPct(v);
    }
    if (column.calculated && column.key === 'bb_pct') {
      const v = currentTab === 'pitching' ? stats?.BB_percent : stats?.bb_percentage;
      return fmtPct(v);
    }
    if (column.key === 'k_bb_ratio') {
      const v = stats?.k_bb_ratio ?? stats?.K_BB_ratio;
      return v === null || v === undefined ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'k_bb_pct') return fmtPct(stats?.K_BB_percent);
    if (column.key === 'lob_pct') return fmtPct(stats?.LOB_percent);
    if (column.key === 'fip') {
      const v = stats?.FIP ?? stats?.fip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'fip_plus') {
      const v = stats?.FIP_plus ?? stats?.fip_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'era_plus') {
      const v = stats?.ERA_plus ?? stats?.era_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'xfip') {
      const v = stats?.xFIP ?? stats?.xfip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'babip_against') {
      const v = stats?.BABIP_against ?? stats?.babip_against;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(3);
    }

    // OPS: use directly from API if available, otherwise calculate
    if (column.calculated && column.key === 'ops') {
      const directOps = stats?.on_base_plus_slugging ?? stats?.ops;
      if (directOps !== undefined && directOps !== null) {
        return formatStat(typeof directOps === 'object' && directOps.$numberDecimal ? parseFloat(directOps.$numberDecimal) : parseFloat(directOps), 'ops');
      }
      const obp = stats?.ob_pct || stats?.onBasePercentage || stats?.on_base_percentage;
      const slg = stats?.slg_pct || stats?.sluggingPercentage || stats?.slugging_percentage;
      return calculateOPS(obp, slg);
    }

    // Context-aware key mapping for pitching stats
    // When on pitching tab, some stats should use "allowed" versions
    let actualKey = column.key;
    if (currentTab === 'pitching') {
      const pitchingKeyMapping = {
        '2b': 'doubles_allowed',
        '3b': 'triples_allowed',
        'hr': 'home_runs_allowed',
        'bb': 'walks_allowed',
      };
      actualKey = pitchingKeyMapping[column.key] || column.key;
    }

    // Define fallback mappings for different data formats
    const fallbackMapping = {
      // Batting stats fallbacks
      'avg': ['avg', 'battingAverage', 'batting_average'],
      'gp': ['gp', 'games_played', 'gamesPlayed'],
      'gs': ['gs', 'games_started', 'gamesStarted'],
      'ab': ['ab', 'atBats', 'at_bats'],
      'r': ['r', 'runs'],
      'h': ['h', 'hits'],
      '2b': ['2b', 'doubles'],
      '3b': ['3b', 'triples'],
      'hr': ['hr', 'home_runs', 'homeRuns'],
      'rbi': ['rbi'],
      'tb': ['tb', 'totalBases', 'total_bases'],
      'slg_pct': ['slg_pct', 'sluggingPercentage', 'slugging_percentage'],
      'bb': ['bb', 'walks'],
      'so': ['so', 'strikeouts'],
      'ob_pct': ['ob_pct', 'onBasePercentage', 'on_base_percentage'],
      'sb': ['sb', 'stolenBases', 'stolen_bases'],
      'cs': ['cs', 'caughtStealing', 'caught_stealing'],
      'hbp': ['hbp', 'hit_by_pitch', 'hitByPitch'],
      'gdp': ['gdp', 'grounded_into_double_play', 'groundedIntoDoublePlay', 'groundIntoDP'],
      'sf': ['sf', 'sacrifice_flies', 'sacFlies'],
      'sh': ['sh', 'sacrifice_hits', 'sacHits'],
      // Pitching stats fallbacks
      'wins': ['w', 'wins'],
      'losses': ['l', 'losses'],
      'era': ['era', 'ERA'],
      'games_pitched': ['gp', 'games_pitched', 'gamesPitched', 'app', 'appearances'],
      'games_started': ['gs', 'games_started', 'gamesStarted'],
      'complete_games': ['cg', 'complete_games', 'completeGames'],
      'shutouts': ['sho', 'shutouts'],
      'saves': ['sv', 'saves'],
      'innings_pitched': ['ip', 'innings_pitched', 'inningsPitched'],
      'hits_allowed': ['h', 'hits_allowed', 'hitsAllowed', 'hits'],
      'runs_allowed': ['r', 'runs_allowed', 'runsAllowed', 'runs'],
      'earned_runs': ['er', 'earned_runs', 'earnedRuns'],
      'walks_allowed': ['bb', 'walks_allowed', 'walksAllowed', 'walks'],
      'strikeouts_pitched': ['so', 'strikeouts_pitched', 'strikeoutsPitched', 'strikeouts'],
      'home_runs_allowed': ['hr', 'home_runs_allowed', 'homeRunsAllowed', 'homeRuns'],
      'doubles_allowed': ['doubles_allowed', 'doublesAllowed', 'doubles'],
      'triples_allowed': ['triples_allowed', 'triplesAllowed', 'triples'],
      'wild_pitches': ['wp', 'wild_pitches', 'wildPitches'],
      'hit_batters': ['hbp', 'hit_batters', 'hitBatters', 'hitByPitch'],
      'balks': ['bk', 'balks'],
      'at_bats_against': ['ab', 'at_bats_against', 'atBatsAgainst', 'atBats'],
      'batting_average_against': ['baa', 'batting_average_against', 'battingAverageAgainst', 'battingAverage'],
      'sacrifice_flies_allowed': ['sacrifice_flies_allowed', 'sacFlies'],
      'sacrifice_hits_allowed': ['sacrifice_hits_allowed', 'sacHits'],
      'WHIP': ['whip', 'WHIP'],
      // Fielding stats fallbacks
      'fielding_percentage': ['fpct', 'fielding_percentage', 'fieldingPercentage'],
      'errors': ['e', 'errors'],
      'putouts': ['po', 'putouts'],
      'assists': ['a', 'assists'],
      'double_plays': ['dp', 'double_plays', 'doublePlays'],
      'total_chances': ['tc', 'total_chances', 'totalChances', 'chances'],
      'stolen_bases_against': ['sba', 'stolen_bases_against', 'stolenBasesAgainst', 'stolenBasesAllowed'],
      'runners_caught_stealing': ['rcs', 'runners_caught_stealing', 'runnersCaughtStealing'],
      'runners_caught_stealing_percentage': ['rcs_pct', 'runners_caught_stealing_percentage', 'runnersCaughtPercentage'],
      'passed_balls': ['pb', 'passed_balls', 'passedBalls'],
      'catcher_interference': ['ci', 'catcher_interference', 'catcherInterference'],
      'fielding_games': ['g', 'fielding_games', 'fieldingGames', 'games'],
      // New advanced stats fallbacks
      'iso': ['iso', 'isolatedPower', 'isolated_power'],
      'woba': ['woba', 'wOBA', 'weighted_on_base_average'],
      'wrc_plus': ['wRC_plus', 'wrc_plus', 'wRC+', 'wrcPlus', 'weighted_runs_created_plus'],
      'pa': ['pa', 'plate_appearances', 'plateAppearances'],
      'babip': ['babip', 'BABIP', 'batting_average_balls_in_play'],
      'ops_plus': ['ops_plus', 'OPS+', 'opsPlus', 'ops_plus_adjusted'],
      'fip': ['fip', 'FIP', 'fielding_independent_pitching'],
      'k_bb_pct': ['k_bb_pct', 'k-bb%', 'kMinusBbPct'],
      'fip_plus': ['fip_plus', 'FIP+', 'fipPlus'],
      'era_plus': ['era_plus', 'ERA+', 'eraPlus'],
      'xfip': ['xfip', 'xFIP'],
      'lob_pct': ['lob_pct', 'LOB%', 'lobPct', 'left_on_base_percentage'],
      'babip_against': ['babip_against', 'babipAgainst', 'BABIP_against'],
    };

    // Try to get value using fallback keys (use actualKey which is context-aware)
    const possibleKeys = fallbackMapping[actualKey] || [actualKey];
    for (const key of possibleKeys) {
      if (stats?.[key] !== undefined && stats?.[key] !== null) {
        return formatStat(stats[key], actualKey);
      }
    }

    // Fallback: check all available stats objects on the player
    const allStatsObjects = [
      Array.isArray(player.battingStats) ? player.battingStats[0] : player.battingStats,
      Array.isArray(player.pitchingStats) ? player.pitchingStats[0] : player.pitchingStats,
      Array.isArray(player.fieldingStats) ? player.fieldingStats[0] : player.fieldingStats,
    ].filter(s => s && s !== stats);

    for (const altStats of allStatsObjects) {
      for (const key of possibleKeys) {
        if (altStats?.[key] !== undefined && altStats?.[key] !== null) {
          return formatStat(altStats[key], actualKey);
        }
      }
    }

    // Last resort: check player-level properties directly
    for (const key of possibleKeys) {
      if (player?.[key] !== undefined && player?.[key] !== null) {
        return formatStat(player[key], actualKey);
      }
    }

    return formatStat(stats?.[actualKey], actualKey);
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-end mb-2 relative">
        <button
          ref={buttonRef}
          onClick={() => setShowFilterModal(!showFilterModal)}
          className="flex items-center gap-2 px-3 py-1.5 bg-jp-section hover:bg-jp-line rounded-lg text-jp-ink text-xs transition-colors"
        >
          <span>Add/Subtract Filters</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>

        {/* Filter Modal Dropdown */}
        <StatsFilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilters}
          buttonRef={buttonRef}
        />
      </div>

      <div className="overflow-x-auto overflow-y-auto max-h-[500px] rounded-lg border border-jp-line scrollbar-thin scrollbar-thumb-jp-line scrollbar-track-jp-section">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-jp-card backdrop-blur-sm">
              {/* Fixed columns */}
              {fixedColumns.map((column, index) => (
                <th key={index} className={`px-3 py-2 text-center font-semibold text-jp-accent-ink uppercase tracking-wide whitespace-nowrap border-b border-jp-line${index === 0 ? ' sticky left-0 z-20 bg-jp-card after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-jp-section' : ''}`}>
                  {column}
                </th>
              ))}
              {/* Dynamic stat columns */}
              {dynamicColumns.map((column, index) => (
                <th key={`stat-${index}`} className="px-3 py-2 text-center font-semibold text-jp-accent-ink uppercase tracking-wide whitespace-nowrap border-b border-jp-line">
                  {column.label}
                </th>
              ))}
              <th className="px-3 py-2 border-b border-jp-line w-12"></th>
            </tr>
          </thead>
          <tbody>
            {followedPlayers && followedPlayers.length > 0 ? (
              followedPlayers.map((player, index) => {
                // Handle different data formats
                const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : str;
                const playerName = toTitleCase(
                  player.name ||
                  (player.firstName && player.lastName ? `${player.firstName} ${player.lastName}` : '') ||
                  player.firstName ||
                  player.lastName ||
                  ''
                ) || 'N/A';
                const position = player.position?.trim() || player.primaryPosition?.trim() || 'N/A';
                const team = player.team?.name || player.team || 'N/A';
                const playerClass = normalizePlayerClass(player.playerClass ) || 'N/A';

                return (
                  <tr key={player._id || index} className="hover:bg-jp-line transition-colors border-b border-jp-line">
                    {/* Fixed columns */}
                    <td className="px-3 py-2 text-jp-ink font-medium whitespace-nowrap sticky left-0 z-[5] bg-jp-section after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-jp-section">{playerName}</td>
                    <td className="px-3 py-2 text-jp-muted whitespace-nowrap">{position}</td>
                    <td className="px-3 py-2 text-jp-muted whitespace-nowrap italic">{team}</td>
                    <td className="px-3 py-2 text-jp-muted whitespace-nowrap uppercase">{playerClass}</td>

                    {/* Dynamic stat columns */}
                    {dynamicColumns.map((column, idx) => (
                      <td key={`stat-${idx}`} className="px-3 py-2 text-jp-muted text-center whitespace-nowrap">
                        {getStatValue(player, column)}
                      </td>
                    ))}

                    {/* Action button */}
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleMessageClick(player)}
                        className="p-1.5 hover:bg-jp-line rounded-lg transition-colors"
                        title="Send message"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-jp-muted hover:text-jp-ink">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={allColumns.length + 1} className="px-3 py-8 text-center text-jp-muted">
                  No followed players to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        /* Webkit browsers (Chrome, Safari, Edge) */
        .scrollbar-thin::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: rgb(var(--jp-section));
          border-radius: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgb(var(--jp-line));
          border-radius: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }

        /* Firefox */
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: rgb(var(--jp-line)) rgb(var(--jp-section));
        }
      `}</style>
    </div>
  );
};
